/**
 * Markdown parsing helpers and utilities
 * @fileoverview Provides functions for parsing YAML front matter, variable replacement, and markdown rendering with various plugins
 */

import yaml from 'js-yaml';
import markdownit from 'markdown-it';
import markdownItAttribution from 'markdown-it-attribution';
import wikilinksPlugin from './markdown-it-wikilinks.js';
import figureCaptionPlugin from './markdown-it-figure-caption.js';
import markdownitFootnote from 'markdown-it-footnote';
import markdownitAbbr from './markdown-it-abbr.js';
import markdownItHighlightjs from 'markdown-it-highlightjs';
import markdownitSub from 'markdown-it-sub';
import markdownitSup from 'markdown-it-sup';
import chartBlocksPlugin from './markdown-it-chart-blocks.js';
import networkVisualizationPlugin from './network-visualization-parser.js';
import todoPlugin from './markdown-it-todo.js';
import asidePlugin from './markdown-it-aside.js';
import blockquoteCitePlugin from './markdown-it-blockquote-cite.js';
import inlineExtrasPlugin from './markdown-it-inline-extras.js';

/**
 * Extracts and parses YAML front matter from a markdown string
 * @param {string} inputString - The markdown content with potential YAML front matter
 * @return {Object|null} Parsed YAML object or null if parsing fails
 */
export function extractYamlFrontMatter(inputString) {
    const frontMatterRegex = /^---\n([\s\S]*?)\n---/;
    const match = inputString.match(frontMatterRegex);
    if (match) {
        // Parse the YAML front matter
        const yamlContent = match[1];
        try {
            return yaml.load(yamlContent);  // Parse YAML content and return as an object
        } catch (e) {
            console.error("Failed to parse YAML front matter:", e);
            return null;
        }
    }
    return {};
}

/**
 * Removes YAML front matter from a markdown string
 * @param {string} inputString - The markdown content with potential YAML front matter
 * @return {string} The markdown content without YAML front matter
 */
export function removeYamlFrontMatter(inputString) {
    const frontMatterRegex = /^---\n([\s\S]*?)\n---/;
    return inputString.replace(frontMatterRegex, '').trim();
}

/** Regular expression pattern for matching variable placeholders like ${variableName} */
const variablePattern = /\$\{[a-zA-Z_][a-zA-Z0-9_-]*\}/g;

/**
 * Replaces variable placeholders in a string with values from attributes object
 * @param {string} str - The string containing variable placeholders
 * @param {Object} attributes - Object containing variable values
 * @return {string} String with variables replaced by their values
 */
function replaceVariables(str, attributes) {
  return str.replace(variablePattern, (match) => {
    const varName = match.slice(2, -1);
    return Object.prototype.hasOwnProperty.call(attributes, varName)
      ? attributes[varName]
      : match;
  });
}

/**
 * Parses markdown content with YAML front matter and various plugins
 * @param {string} content - The markdown content to parse
 * @param {Object} [attributes={}] - Additional attributes for processing
 * @return {Promise<Object>} Object containing parsed data and rendered HTML
 */
export async function parseDataroomMarkup(content, attributes = {}) {

  const md = markdownit({
    html: true,
    breaks: true,
    linkify: true,
    typographer: true
  }).use(markdownItAttribution, {
    marker: 'cite:',
  })
  .use(wikilinksPlugin, { wikilinksSearchPrefix: attributes['wikilinks-search-prefix'] })
  .use(figureCaptionPlugin)
  .use(markdownitFootnote)
  .use(markdownitAbbr)
  .use(networkVisualizationPlugin)
  .use(markdownItHighlightjs)
  .use(markdownitSub)
  .use(markdownitSup)
  .use(chartBlocksPlugin)
  .use(todoPlugin)
  .use(asidePlugin)
  .use(blockquoteCitePlugin)
  .use(inlineExtrasPlugin);

  // add line numbers to list items
  const originalListItemOpen = md.renderer.rules.list_item_open || function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.list_item_open = function(tokens, idx, options, env, self) {
    const token = tokens[idx];
    if (token.map && token.map.length) {
      token.attrSet('data-line', String(token.map[0]));
    }
    return originalListItemOpen(tokens, idx, options, env, self);
  };

  const data = extractYamlFrontMatter(content);
  const template_without_yaml = removeYamlFrontMatter(content);
  const new_value = replaceVariables(template_without_yaml, data)
  const renderedContent = md.render(new_value);
  return {data:data, html: renderedContent};
}
